import {ColorPicker} from '@github-ui/color-picker'
import {EmojiAutocomplete} from '@github-ui/emoji-autocomplete'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {testIdProps} from '@github-ui/test-id-props'
import {useDynamicTextareaHeight} from '@github-ui/use-dynamic-textarea-height'
import {Button, FormControl, Textarea, TextInput, useOnOutsideClick} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import DOMPurify from 'dompurify'
import {type FormEvent, forwardRef, type KeyboardEvent, useReducer, useRef, useState} from 'react'

import {replaceShortCodesWithEmojis} from './emojis'
import {VALIDATION_MESSAGES} from './resources'
import styles from './SingleSelectOptionModal.module.css'
import {DOMPURIFY_CONFIG, SingleSelectToken} from './SingleSelectToken'
import type {NewSingleSelectOption, SingleSelectOption, SingleSelectOptionWithId} from './types'
import {getValidationErrors, isPlatformMeta} from './util'

export type BaseSingleSelectOptionModalProps = {
  initialOption: SingleSelectOption
  maxLength?: number
  onSave: (updatedOption: SingleSelectOption) => void
  onCancel: () => void
  /**
   * When true, enable length validation with visual feedback. Users can type past the 50 character limit
   * and see validation errors, but submission is prevented while the name exceeds the limit.
   */
  enableNameLengthValidation: boolean
  /**
   * Function used to preprocess the SingleSelectOption name before it's assigned to nameHtml for preview.
   * The result is then sanitized by UnsafeHTMLText using DOMPurify to prevent XSS attacks.
   * If not provided, a strict default sanitizer is used that prohibits most HTML tags, attributes, and styling.
   */
  preSanitizeHtml?: (value: string) => SafeHTMLString
}

type SingleSelectOptionModalPropsWithDuplicateCheck = BaseSingleSelectOptionModalProps & {
  noDuplicatedOptions: true
  options: SingleSelectOptionWithId[]
}

type SingleSelectOptionModalPropsWithoutDuplicateCheck = BaseSingleSelectOptionModalProps & {
  noDuplicatedOptions: false
}

export type SingleSelectOptionModalProps =
  | SingleSelectOptionModalPropsWithDuplicateCheck
  | SingleSelectOptionModalPropsWithoutDuplicateCheck

/** Submit on cmd/ctrl enter. */
const onKeyDown = (ev: KeyboardEvent<HTMLFormElement>) => {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  if (ev.key === 'Enter' && isPlatformMeta(ev)) ev.currentTarget.requestSubmit()
}

/**
 * Default HTML preprocessor for the SingleSelectOption name preview.
 * If `preSanitizeHtml` prop is not provided, this function is used to sanitize the HTML.
 */
const defaultHtmlPreprocessor = (html: string) => {
  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG) as SafeHTMLString
}

/**
 * Remove extra fields such as `nameHtml` and `descriptionHtml` so we don't display those outdated values. Consumers
 * should reapply those values, ie by spreading them back onto the object.
 */
const stripExtraFields = ({name, color, description}: SingleSelectOption): NewSingleSelectOption => ({
  name,
  color,
  description,
})

const initializeOption = (option: SingleSelectOption, preSanitizeHtml: (input: string) => string) => {
  return {
    ...stripExtraFields(option),
    nameHtml: preSanitizeHtml(option.name),
  }
}

export const SingleSelectOptionModal = ({
  initialOption,
  maxLength,
  onSave,
  onCancel,
  noDuplicatedOptions,
  enableNameLengthValidation = false,
  ...props
}: SingleSelectOptionModalProps) => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const preSanitizeHtml = props.preSanitizeHtml ?? defaultHtmlPreprocessor

  /** Works like useState but instead of overwriting the full value, we merge in a partial value. */
  const [option, updateOption] = useReducer(
    (current: SingleSelectOption, changes: Partial<SingleSelectOption>) => {
      setShowValidationErrors(false)
      return {...current, ...changes}
    },
    initializeOption(initialOption, preSanitizeHtml),
  )

  const validationErrors = getValidationErrors(
    option.name,
    'options' in props && Array.isArray(props.options) ? props.options : [],
    noDuplicatedOptions,
    enableNameLengthValidation,
    initialOption.name,
  )
  const isDuplicate = validationErrors.some(error => error.type === 'duplicate')
  const isTooLong = validationErrors.some(error => error.type === 'tooLong')

  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault() // prevent page reload

    // Client-side validation fallback for when HTML5 validation doesn't work (e.g., in tests)
    if (option.name.trim() === '' || isTooLong) {
      setShowValidationErrors(true)
      return
    }

    onSave({
      ...stripExtraFields(option),
      name: replaceShortCodesWithEmojis(option.name),
      description: replaceShortCodesWithEmojis(option.description ?? ''),
    })
  }

  const onInvalid = (ev: FormEvent) => {
    ev.preventDefault() // prevent native validation messages
    setShowValidationErrors(true)
  }

  const containerRef = useRef<HTMLDivElement>(null)

  // Dialog doesn't use Overlay or call `useOnOutsideClick` under the hood. This means that
  // it doesn't get registered as an overlay in the stack, which means it doesn't intercept
  // and stop propagating clicks. This means that if there are any open `Overlay`s underneath
  // this `Dialog`, clicks in this dialog will register as clicks outside of those overlays,
  // causing them to try to close.
  // We can prevent this by registering a fake `useOnClickOutside` hook to make this look like
  // a regular `Overlay`. Clicks inside will no-op and the hook will prevent them from propagating
  // to other overlays; clicks outside need to have `preventDefault` called on them to stop them
  // from propagating.
  useOnOutsideClick({containerRef, onClickOutside: e => e.preventDefault()})

  const handleNameChange = (name: string) => {
    updateOption({name, nameHtml: preSanitizeHtml(name)})
  }

  return (
    <Dialog
      title={initialOption.name ? 'Edit option' : 'New option'}
      onClose={onCancel}
      width="medium"
      ref={containerRef}
      aria-label={initialOption.name ? 'Edit option dialog' : 'New option dialog'}
      renderBody={() => (
        // By default there is no way to wrap the footer and body in one element, so we
        // have to custom-render the footer inside the body. Otherwise we can't use `submit`
        // button to submit the form
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <form
          onSubmit={onSubmit}
          onInvalid={onInvalid}
          onKeyDown={onKeyDown}
          // Stopping blur propagation fixes a bug where the table thinks the cell editor was
          // blurred when it was really an input inside this dialog.
          onBlur={e => e.stopPropagation()}
          className={styles.SingleSelectOptionModalForm}
        >
          <Dialog.Body className={styles.SingleSelectOptionModalDialogBody}>
            <LabelPreview option={option} />

            <NameInput
              value={option.name}
              onChange={handleNameChange}
              showValidation={showValidationErrors}
              showDuplicatedError={isDuplicate}
              showTooLongError={isTooLong}
            />

            <ColorPicker value={option.color} onChange={color => updateOption({color})} label="Color" />

            <DescriptionInput
              value={option.description}
              maxLength={maxLength}
              onChange={description => updateOption({description})}
              showValidation={showValidationErrors}
            />
          </Dialog.Body>
          <Dialog.Footer>
            <Button onClick={onCancel} className={styles.SingleSelectOptionModalCancelButton}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              {...testIdProps('save-single-select-option-button')}
              disabled={isDuplicate || isTooLong}
            >
              Save
            </Button>
          </Dialog.Footer>
        </form>
      )}
    />
  )
}

interface InputProps {
  value: string
  onChange: (value: string) => void
  showValidation: boolean
}

interface DescriptionInputProps extends InputProps {
  maxLength?: number
}

interface NameInputProps extends InputProps {
  showDuplicatedError: boolean
  showTooLongError: boolean
}

const NameInput = forwardRef<HTMLInputElement, NameInputProps>(function NameInput(
  {value, onChange, showValidation, showDuplicatedError, showTooLongError},
  ref,
) {
  const showRequiredError = showValidation && value.trim() === ''

  return (
    <FormControl required>
      <FormControl.Label>Label text</FormControl.Label>

      <EmojiAutocomplete fullWidth>
        <TextInput
          ref={ref}
          value={value}
          onChange={ev => onChange(ev.target.value)}
          block
          name="Label text"
          validationStatus={showRequiredError ? 'error' : undefined}
          aria-invalid={showRequiredError ? 'true' : undefined}
          pattern=".*\S+.*" // disallow whitespace-only labels
          {...testIdProps('single-select-option-text-input')}
        />
      </EmojiAutocomplete>

      {showRequiredError && <FormControl.Validation variant="error">Label text is required</FormControl.Validation>}
      {showDuplicatedError && (
        <FormControl.Validation variant="error">Option has already been taken</FormControl.Validation>
      )}
      {showTooLongError && (
        <FormControl.Validation variant="error">{VALIDATION_MESSAGES.TOO_LONG}</FormControl.Validation>
      )}
    </FormControl>
  )
})

const DescriptionInput = ({value, onChange, maxLength}: DescriptionInputProps) => {
  const elementRef = useRef<HTMLTextAreaElement | null>(null)

  const heightStyle = useDynamicTextareaHeight({
    minHeightLines: 0,
    maxHeightLines: 12,
    elementRef,
    value,
  })

  return (
    <FormControl>
      <FormControl.Label>Description</FormControl.Label>
      <EmojiAutocomplete fullWidth>
        <Textarea
          ref={elementRef}
          value={value}
          onChange={ev => onChange(ev.target.value)}
          block
          name="Description"
          // Don't worry about showing validation errors for this because the browser should
          // typically prevent the user from typing more than maxLength characters. If it
          // doesn't, we'll catch it server-side.
          maxLength={maxLength}
          style={{
            '--textarea-height': heightStyle.height,
            '--textarea-min-height': heightStyle.minHeight,
            '--textarea-max-height': heightStyle.maxHeight,
            '--textarea-box-sizing': heightStyle.boxSizing,
            '--textarea-field-sizing': heightStyle.fieldSizing,
          }}
          className={clsx(styles.SingleSelectOptionModalTextarea)}
        />
      </EmojiAutocomplete>
      <FormControl.Caption>Visible in group headers and value pickers</FormControl.Caption>
    </FormControl>
  )
}

interface LabelPreviewProps {
  option: SingleSelectOption
}

const LabelPreview = ({option}: LabelPreviewProps) => (
  <figure className={styles.SingleSelectOptionModalPreview}>
    <figcaption className="sr-only">Preview</figcaption>
    <SingleSelectToken option={option} />
  </figure>
)

try{ SingleSelectOptionModal.displayName ||= 'SingleSelectOptionModal' } catch {}
try{ NameInput.displayName ||= 'NameInput' } catch {}
try{ DescriptionInput.displayName ||= 'DescriptionInput' } catch {}
try{ LabelPreview.displayName ||= 'LabelPreview' } catch {}